
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { AppConfig } from 'src/app/app.config';
import { of, Observable, BehaviorSubject, map } from 'rxjs';

export class SettingsValue {
    ApplicationTypeId: number;
    SettingsTypeId: number;
    SettingsTargetId: string;
    SettingsNameId: number;
    Value: string;
    ObjectId: string;
    CompanyId: string;
    Created: string;
    CreatorId: string;
    CreatorName: null | string;
    Modified: string;
    ModifierId: string;
    ModifierName: string;
    Archived: null | string;
    ArchiverId: string;
    ArchiverName: null | string;
}

@Injectable({
    providedIn: 'root'
})
export class CompanySettingsService {

    private settingsApiUrl = this.config.backendPortalUri + 'Settings.WebAPI/api/Settings/';
    private readonly customerAdminUri = this.config.backendIwmUri + 'CustomerAdminApi/';
    private iwmApplicationType: number = 15 // IWM
    private settingsList: { SettingsNameId: number, Value: string }[] = [];

    private companyTucaSettings: Array<CompanyTaskUnitCostAccounting> = [];
    private companyTaskCostAccountingSettings: Array<CompanyTaskCostAccounting> = [];
    private _descriptionIncludedInQuickSearch$ = new BehaviorSubject<string>("false");

    public readonly descriptionIncludedInQuickSearch$ = this._descriptionIncludedInQuickSearch$.asObservable();

    constructor(private http: HttpClient, private config: AppConfig) { }

    /**
     * Load all company settings
     */
    public loadAll() {
        this.loadSettings().subscribe((res) => {
            this.settingsList = res;

            // These methods only filter lists based on cost accounting type
            this.setCompanyTucaSettings();
            this.setCompanyTaskCostAccountingSettings();
            this.setQuickSearch();
        })
    }

    /**
     * Enable quick search to search in the description field on listPages
     * Setting: IncludeDescriptionInQuickSearch = 2555
     */
    private setQuickSearch() {
        const setting = this.settingsList.find(o => o.SettingsNameId === 2555);
        if (!setting) {
            this._descriptionIncludedInQuickSearch$.next("false");
        } else {
            this._descriptionIncludedInQuickSearch$.next(setting.Value);
        }
    }

    /**
     * Get specific company setting value
     * @param settingName number
     */
    public getSettingValue(settingName: number): Observable<string> {
        let setting = this.settingsList.find(item => item.SettingsNameId === settingName);
        let value = '';
        if (setting !== undefined) {
            value = setting.Value;
        }

        return of(value);
    }

    private loadSettings(): Observable<any> {
        const urlParameters = `?applicationType=${this.iwmApplicationType}`;
        const url = this.settingsApiUrl + 'getSettingsValues' + urlParameters;
        return this.http.get<SettingsValue>(url);
    }


    /* TaskUnitCostAccountings region */

    /**
     * Returns required TaskUnitCostAccounting types for currentuser's company as a string array
     */
    public getRequiredTaskUnitCostAccountingTypes(): Array<string> {
        return this.companyTucaSettings.filter(o => o.RequiredOnIpAccept || o.RequiredOnIpSend || o.RequiredOnTaskSend)
            .map(o => o.Types)
            .reduce((acc, value) => acc.concat(value), []);
    }

    /**
     * Returns required TaskUnitCostAccounting types for currentuser's company as a string array
     */
    public getTaskUnitCostAccountingTypesInUse(): Array<string> {
        return this.companyTucaSettings.filter(o => o.InUse)
            .map(o => o.Types)
            .reduce((acc, value) => acc.concat(value), []);
    }

    /**
     * Forms an array of TaskUnitCostAccounting settings for currentuser's company using values loaded from Portal settings
     */
    private setCompanyTucaSettings(): void {

        this.companyTucaSettings = [];

        PortalCompanySettings.TaskUnitCostAccountingConfigurations.forEach(o => {
            if (this.getSetting(o.InUseSettingName)) {
                this.companyTucaSettings.push(new CompanyTaskUnitCostAccounting(
                    o.Types,
                    true,
                    this.getSetting(o.TaskSendSettingName),
                    this.getSetting(o.IpSendSettingName),
                    this.getSetting(o.IpAcceptSettingName))
                );
            }
        });
    }

    private getSetting(settingName: number): boolean {
        let setting = this.settingsList.find(item => item['SettingsNameId'] === settingName);
        let value = false;
        if (setting !== undefined && setting !== null) {
            value = Boolean(JSON.parse(setting.Value));
        }
        return value;
    }

    /* end of TaskUnitCostAccountings region */



    /**
     * Returns required TaskCostAccounting types for currentuser's company as a string array
     * E.g. Classifications
     */
    public getRequiredTaskCostAccountingTypes(): Array<CompanyTaskCostAccounting> {
        return this.companyTaskCostAccountingSettings;
    }

    /**
     * Adds TaskCostAccounting settings into the array
     * so all specific settings can be retrieved at once, when needed.
     * It retrieves settings from settingsList which includes all IWM settings
     */
    private setCompanyTaskCostAccountingSettings(): void {

        this.companyTaskCostAccountingSettings = [];

        PortalCompanySettings.TaskCostAccountingConfigurations.forEach(o => {
            // add setting in array if is enabled
            if (this.getSetting(o.InUseSettingName)) {
                this.companyTaskCostAccountingSettings.push(
                    new CompanyTaskCostAccounting(
                        o.Type,
                        true,
                        this.getSetting(o.TaskSendSettingName),
                        this.getSetting(o.IpSendSettingName),
                        this.getSetting(o.IpAcceptSettingName)
                    ));
            }
        });
    }

    /**
     * Returns task owners unit sort order setting value for tasktype
     */
    public getUnitSortOrderSettingStatus(taskId: string): Observable<any> {
        return this.http.get<any>(`${this.customerAdminUri}TaskTypeSpecificConfigurations/Default.GetUnitSortOrderByTaskId(taskId=${taskId})`)
            .pipe(
                map(x => x['value'])
            );
    }
}


/**
 * Class for handling single TaskUnitCostAccounting configuration
 * for specific company
 */
class CompanyTaskUnitCostAccounting {
    public Types: Array<string>;
    public InUse: boolean;
    public RequiredOnTaskSend: boolean;
    public RequiredOnIpSend: boolean;
    public RequiredOnIpAccept: boolean;

    constructor(types: string[], inUse: boolean, reqOnTaskSend: boolean, reqOnIpSend: boolean, reqOnIpAccept: boolean) {
        this.Types = types;
        this.InUse = inUse;
        this.RequiredOnTaskSend = reqOnTaskSend;
        this.RequiredOnIpSend = reqOnIpSend;
        this.RequiredOnIpAccept = reqOnIpAccept;
    }
}

/**
 * Class for handling single TaskUnitCostAccounting configuration
 * for specific company
 */
export class CompanyTaskCostAccounting {
    public Type: string;
    public InUse: boolean;
    public RequiredOnTaskSend: boolean;
    public RequiredOnIpSend: boolean;
    public RequiredOnIpAccept: boolean;
    constructor(type: string, inUse: boolean, reqOnTaskSend: boolean, reqOnIpSend: boolean, reqOnIpAccept: boolean) {
        this.Type = type;
        this.InUse = inUse;
        this.RequiredOnTaskSend = reqOnTaskSend;
        this.RequiredOnIpSend = reqOnIpSend;
        this.RequiredOnIpAccept = reqOnIpAccept;
    }
}

/**
 * Class for TaskUnitCostAccounting configurations defined in Portal settings
 */
class TaskUnitCostAccountingConfiguration {
    public Types: Array<string>;
    public InUseSettingName: number;
    public TaskSendSettingName: number;
    public IpSendSettingName: number;
    public IpAcceptSettingName: number;

    constructor(types: string[], inUse: number, taskSend: number, ipSend: number, ipAccept: number) {
        this.Types = types;
        this.InUseSettingName = inUse;
        this.TaskSendSettingName = taskSend;
        this.IpSendSettingName = ipSend;
        this.IpAcceptSettingName = ipAccept;
    }
}

/**
 * Class for TaskCostAccounting configurations defined in Portal settings
 */
class TaskCostAccountingConfiguration {
    public Type: string;
    public InUseSettingName: number;
    public TaskSendSettingName: number;
    public IpSendSettingName: number;
    public IpAcceptSettingName: number;

    constructor(type: string, inUse: number, taskSend: number, ipSend: number, ipAccept: number) {
        this.Type = type;
        this.InUseSettingName = inUse;
        this.TaskSendSettingName = taskSend;
        this.IpSendSettingName = ipSend;
        this.IpAcceptSettingName = ipAccept;
    }
}

class PortalCompanySettings {
    static TaskUnitCostAccountingConfigurations: Array<TaskUnitCostAccountingConfiguration> = [
        new TaskUnitCostAccountingConfiguration(['MainTaskUnitCostAccounting'], 2003, 2514, null, 2004),
        new TaskUnitCostAccountingConfiguration(['SubTaskUnitCostAccounting'], 2003, 2515, null, 2516),
        new TaskUnitCostAccountingConfiguration(['DeliveryDateCostAccounting'], 2005, 2012, 2020, 2028),
        new TaskUnitCostAccountingConfiguration(['BusinessAreaCostAccounting'], 2006, 2014, 2022, 2030),
        new TaskUnitCostAccountingConfiguration(['FunctionTaskUnitCostAccounting'], 2007, 2013, 2021, 2029),
        new TaskUnitCostAccountingConfiguration(['ProjectTaskUnitCostAccounting'], 2008, 2016, 2024, 2032),
        new TaskUnitCostAccountingConfiguration(['DescriptionTaskUnitCostAccounting'], 2009, 2015, 2023, 2031),
        new TaskUnitCostAccountingConfiguration(['OperatingLocationTaskUnitCostAccounting'], 2010, 2017, 2025, 2033),
        new TaskUnitCostAccountingConfiguration(['SubProjectTaskUnitCostAccounting'], 2069, 2070, 2071, 2072),
        new TaskUnitCostAccountingConfiguration(['MainProjectIdentifierTaskUnitCostAccounting'], 2073, 2074, 2076, 2078),
        new TaskUnitCostAccountingConfiguration(['MainProjectNameTaskUnitCostAccounting'], 2073, 2074, 2076, 2078),
        new TaskUnitCostAccountingConfiguration(['ProjectIdentifierTaskUnitCostAccounting'], 2073, 2075, 2077, 2079),
        new TaskUnitCostAccountingConfiguration(['ProjectNameTaskUnitCostAccounting'], 2073, 2075, 2077, 2079),
        new TaskUnitCostAccountingConfiguration(['DestinationTaskUnitCostAccounting'], 2536, 2537, 2538, 2539),
        new TaskUnitCostAccountingConfiguration(['AccountTaskUnitCostAccounting'], 2510, 2511, 2512, 2513),
        new TaskUnitCostAccountingConfiguration(['LitteraTaskUnitCostAccounting'], 2526, 2527, 2528, 2529),
        new TaskUnitCostAccountingConfiguration(['NoteTaskUnitCostAccounting'], 2542, 2543, 2544, 2545),
        new TaskUnitCostAccountingConfiguration(['TaxRateTaskUnitCostAccounting'], 2546, 2547, 2548, 2549),
        new TaskUnitCostAccountingConfiguration(['ExpenseTypeTaskUnitCostAccounting'], 2551, 2552, 2553, 2554),
    ];

    static TaskCostAccountingConfigurations: Array<TaskCostAccountingConfiguration> = [
        new TaskCostAccountingConfiguration('OrderClassificationName', 2001, 2043, null, 2044),
        new TaskCostAccountingConfiguration('InvoiceClassificationName', 2000, 2045, null, 2046),
        new TaskCostAccountingConfiguration('AccountClassificationName', 2048, 2049, 2051, 2050),
        new TaskCostAccountingConfiguration('CostCenterClassificationName', 2052, 2053, 2055, 2054),
        new TaskCostAccountingConfiguration('PayerReferenceClassificationName', 2057, 2058, 2059, 2060),
        new TaskCostAccountingConfiguration('OrderTypeClassificationName', 2061, 2062, 2063, 2064),
        new TaskCostAccountingConfiguration('WorkQualityClassificationName', 2065, 2066, 2067, 2068),
        new TaskCostAccountingConfiguration('MainProjectNameClassificationName', 2073, 2074, 2076, 2078),
        new TaskCostAccountingConfiguration('ProjectNameClassificationName', 2073, 2075, 2077, 2079),
        new TaskCostAccountingConfiguration('ProjectNumberClassificationName', 2081, 2507, 2508, 2509),
        new TaskCostAccountingConfiguration('TrackingNumberClassificationName', 2083, 2084, 2085, 2086),
        new TaskCostAccountingConfiguration('InvestmentNumberClassificationName', 2087, 2088, 2089, 2090),
        new TaskCostAccountingConfiguration('InvestmentSpecificationClassificationName', 2091, 2092, 2093, 2094),
        new TaskCostAccountingConfiguration('QualityDeviationClassificationName', 2095, null, null, null),
        new TaskCostAccountingConfiguration('WorkValueClassificationName', 2096, 2097, 2098, 2099),
        new TaskCostAccountingConfiguration('MainCostCenterNameClassificationName', 2500, 2501, 2502, 2503),
        new TaskCostAccountingConfiguration('ProjectGroupClassificationName', 2500, 2504, 2505, 2506),
        new TaskCostAccountingConfiguration('OperationCodeClassificationName', 2518, 2519, 2520, 2521),
        new TaskCostAccountingConfiguration('PartnerCodeClassificationName', 2522, 2523, 2524, 2525),
        new TaskCostAccountingConfiguration('LitteraClassificationName', 2526, 2527, 2528, 2529)
    ];
}

export enum SettingName {
    PayerCompanyInUse = 2042,
    InvoicerCompanyInUse = 2080,

    TwoLevelInUse = 2003,
    MainTaskUnitCostAccountingRequiredOnTaskSend = 2514,
    SubTaskUnitCostAccountingRequiredOnTaskSend = 2515,
    SubTaskUnitCostAccountingRequiredOnInvoicingProposalAccept = 2516,
    MainTaskUnitCostAccountingRequiredOnInvoicingProposalAccept = 2004,

    DeliveryDateInUse = 2005,
    DeliveryDateRequiredOnTaskSend = 2012,
    DeliveryDateRequiredOnIpSend = 2020,
    DeliveryDateRequiredOnIpAccept = 2028,

    BusinessAreaInUse = 2006,
    BusinessAreaRequiredOnTaskSend = 2014,
    BusinessAreaRequiredOnIpSend = 2022,
    BusinessAreaRequiredOnIpAccept = 2030,

    FunctionInUse = 2007,
    FunctionRequiredOnTaskSend = 2013,
    FunctionRequiredOnIpSend = 2021,
    FunctionRequiredOnIpAccept = 2029,

    ProjectInUse = 2008,
    ProjectRequiredOnTaskSend = 2016,
    ProjectRequiredOnIpSend = 2024,
    ProjectRequiredOnIpAccept = 2032,

    DescriptionInUse = 2009,
    DescriptionRequiredOnTaskSend = 2015,
    DescriptionRequiredOnIpSend = 2023,
    DescriptionRequiredOnIpAccept = 2031,

    OperatingLocationInUse = 2010,
    OperatingLocationRequiredOnTaskSend = 2017,
    OperatingLocationRequiredOnIpSend = 2025,
    OperatingLocationRequiredOnIpAccept = 2033,

    ScheduledPaymentsInUse = 2040,

    OrderClassificationInUse = 2001,
    OrderClassificationRequiredOnTaskSend = 2043,
    OrderClassificationRequiredOnInvoicingProposalAccept = 2044,

    InvoiceClassificationInUse = 2000,
    InvoiceClassificationRequiredOnTaskSend = 2045,
    InvoiceClassificationRequiredOnInvoicingProposalAccept = 2046,

    AccountInUse = 2048,
    AccountRequiredOnTasksend = 2049,
    AccountRequiredOnInvoicingProposalSend = 2051,
    AccountRequiredOnInvoicingProposalAccept = 2050,
    CostCenterInUse = 2052,
    CostCenterRequiredOnTasksend = 2053,
    CostCenterRequiredOnInvoicingProposalSend = 2055,
    CostCenterRequiredOnInvoicingProposalAccept = 2054,
    PayerReferenceInUse = 2057,
    PayerReferenceRequiredOnTaskSend = 2058,
    PayerReferenceRequiredOnInvoicingProposalSend = 2059,
    PayerReferenceRequiredOnInvoicingProposalAccept = 2060,
    OrderTypeInUse = 2061,
    OrderTypeRequiredOnTaskSend = 2062,
    OrderTypeRequiredOnInvoicingProposalSend = 2063,
    OrderTypeRequiredOnInvoicingProposalAccept = 2064,
    WorkQualityInUse = 2065,
    WorkQualityRequiredOnTaskSend = 2066,
    WorkQualityRequiredOnInvoicingProposalSend = 2067,
    WorkQualityRequiredOnInvoicingProposalAccept = 2068,

    SubProjectInUse = 2069,
    SubProjectRequiredOnTaskSend = 2070,
    SubProjectRequiredOnInvoicingProposalSend = 2071,
    SubProjectRequiredOnInvoicingProposalAccept = 2072,

    TwoLevelProjectTaskCostAccountingInUse = 2073,
    MainProjectTaskCostAccountingRequiredOnTaskSend = 2074,
    ProjectTaskCostAccountingRequiredOnTaskSend = 2075,
    MainProjectTaskCostAccountingRequiredOnInvoicingProposalSend = 2076,
    ProjectTaskCostAccountingRequiredOnInvoicingProposalSend = 2077,
    MainProjectTaskCostAccountingRequiredOnInvoicingProposalAccept = 2078,
    ProjectTaskCostAccountingRequiredOnInvoicingProposalAccept = 2079,

    ProjectNumberInUse = 2081,
    ProjectNumberRequiredOnTaskSend = 2507,
    ProjectNumberRequiredOnInvoicingProposalSend = 2508,
    ProjectNumberRequiredOnInvoicingProposalAccept = 2509,

    TrackingNumberTaskCostAccountingInUse = 2083,
    TrackingNumberTaskCostAccountingRequiredOnTaskSend = 2084,
    TrackingNumberTaskCostAccountingRequiredOnInvoicingProposalSend = 2085,
    TrackingNumberTaskCostAccountingRequiredOnInvoicingProposalAccept = 2086,

    InvestmentNumberTaskCostAccountingInUse = 2087,
    InvestmentNumberTaskCostAccountingRequiredOnTaskSend = 2088,
    InvestmentNumberTaskCostAccountingRequiredOnInvoicingProposalSend = 2089,
    InvestmentNumberTaskCostAccountingRequiredOnInvoicingProposalAccept = 2090,

    InvestmentSpecificationTaskCostAccountingInUse = 2091,
    InvestmentSpecificationTaskCostAccountingRequiredOnTaskSend = 2092,
    InvestmentSpecificationTaskCostAccountingRequiredOnInvoicingProposalSend = 2093,
    InvestmentSpecificationTaskCostAccountingRequiredOnInvoicingProposalAccept = 2094,

    QualityDeviationCodingInUse = 2095,

    WorkValueInUse = 2096,
    WorkValueRequiredOnTaskSend = 2097,
    WorkValueRequiredOnIpSend = 2098,
    WorkValueRequiredOnIpAccept = 2099,

    MainCostCenterProjectGroupsInUse = 2500,
    MainCostCenterRequiredOnTaskSend = 2501,
    MainCostCenterRequiredOnInvoicingProposalSend = 2502,
    MainCostCenterRequiredOnInvoicingProposalAccept = 2503,
    ProjectGroupRequiredOnTaskSend = 2504,
    ProjectGroupRequiredOnInvoicingProposalSend = 2505,
    ProjectGroupRequiredOnInvoicingProposalAccept = 2506,

    AccountTaskUnitCostAccountingInUse = 2510,
    AccountTaskUnitCostAccountingsRequiredOnTaskSend = 2511,
    AccountTaskUnitCostAccountingsRequiredOnInvoicingProposalSend = 2512,
    AccountTaskUnitCostAccountingsRequiredOnInvoicingProposalAccept = 2513,

    OperationCodeInUse = 2518,
    OperationCodeRequiredOnTaskSend = 2519,
    OperationCodeRequiredOnInvoicingProposalSend = 2520,
    OperationCodeRequiredOnInvoicingProposalAccept = 2521,

    PartnerCodeInUse = 2522,
    PartnerCodeRequiredOnTaskSend = 2523,
    PartnerCodeRequiredOnInvoicingProposalSend = 2524,
    PartnerCodeRequiredOnInvoicingProposalAccept = 2525,

    LitteraInUse = 2526,
    LitteraRequiredOnTaskSend = 2527,
    LitteraRequiredOnInvoicingProposalSend = 2528,
    LitteraRequiredOnInvoicingProposalAccept = 2529,

    DestinationTaskUnitCostAccountingInUse = 2536,
    DestinationTaskUnitCostAccountingsRequiredOnTaskSend = 2537,
    DestinationTaskUnitCostAccountingsRequiredOnInvoicingProposalSend = 2538,
    DestinationTaskUnitCostAccountingsRequiredOnInvoicingProposalAccept = 2539,

    NoteTaskUnitCostAccountingInUse = 2542,
    NoteTaskUnitCostAccountingRequiredOnTaskSend = 2543,
    NoteTaskUnitCostAccountingRequiredOnIpSend = 2544,
    NoteTaskUnitCostAccountingRequiredOnIpAccept = 2545,

    TaxRateTaskUnitCostAccountingInUse = 2546,
    TaxRateTaskUnitCostAccountingRequiredOnTaskSend = 2547,
    TaxRateTaskUnitCostAccountingRequiredOnIpSend = 2548,
    TaxRateTaskUnitCostAccountingRequiredOnIpAccept = 2549,

    OrderingApproval = 2550, // Not used at the moment

    ExpenseTypeTaskUnitCostAccountingsInUse = 2551,
    ExpenseTypeIsRequiredOnTasksend = 2552,
    ExpenseTypeIsRequiredOnInvoicingProposalSend = 2553,
    ExpenseTypeIsRequiredOnInvoicingProposalAccept = 2554,
}
